import type { DagDefinition, Edge, GraphLayout, Settings, StepNode, StepRetryPolicy } from './types';

export function safeParseJson(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) return {};
  return JSON.parse(trimmed);
}

export function coerceObject(value: unknown): Record<string, any> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : {};
}

export function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return '{}';
  }
}

export function nextStepId(existing: Set<string>) {
  let i = existing.size + 1;
  while (existing.has(`step_${i}`)) i++;
  return `step_${i}`;
}

export function computeTreeLayout(nodes: StepNode[], edges: Edge[]): GraphLayout {
  return computeTreeLayoutInternal(nodes, edges, 'horizontal');
}

export function computeTreeLayoutVertical(nodes: StepNode[], edges: Edge[]): GraphLayout {
  return computeTreeLayoutInternal(nodes, edges, 'vertical');
}

function computeTreeLayoutInternal(nodes: StepNode[], edges: Edge[], orientation: 'horizontal' | 'vertical'): GraphLayout {
  const nodeIds = nodes.map((n) => n.id);
  const nodeSet = new Set(nodeIds);
  const outgoing = new Map<string, string[]>();
  const incoming = new Map<string, string[]>();
  const inDegree = new Map<string, number>();

  for (const id of nodeIds) {
    outgoing.set(id, []);
    incoming.set(id, []);
    inDegree.set(id, 0);
  }

  let invalidEdges = 0;
  for (const e of edges) {
    if (!nodeSet.has(e.from) || !nodeSet.has(e.to)) {
      invalidEdges++;
      continue;
    }
    outgoing.get(e.from)!.push(e.to);
    incoming.get(e.to)!.push(e.from);
    inDegree.set(e.to, (inDegree.get(e.to) || 0) + 1);
  }

  // Kahn topological sort
  const queue: string[] = nodeIds.filter((id) => (inDegree.get(id) || 0) === 0).sort();
  const topo: string[] = [];
  const inDeg = new Map(inDegree);
  while (queue.length > 0) {
    const id = queue.shift()!;
    topo.push(id);
    for (const to of outgoing.get(id) || []) {
      inDeg.set(to, (inDeg.get(to) || 0) - 1);
      if ((inDeg.get(to) || 0) === 0) {
        queue.push(to);
        queue.sort();
      }
    }
  }

  const hasCycle = topo.length !== nodeIds.length;
  const order = hasCycle ? [...nodeIds].sort() : topo;

  // Levels: longest path depth from sources.
  const level = new Map<string, number>();
  for (const id of order) level.set(id, 0);
  if (!hasCycle) {
    for (const id of order) {
      const base = level.get(id) || 0;
      for (const to of outgoing.get(id) || []) {
        const next = Math.max(level.get(to) || 0, base + 1);
        level.set(to, next);
      }
    }
  }

  const maxLevel = Math.max(0, ...order.map((id) => level.get(id) || 0));

  // Group by level, preserving order for stable alignment.
  const groups: Record<number, string[]> = {};
  for (const id of order) {
    const l = level.get(id) || 0;
    if (!groups[l]) groups[l] = [];
    groups[l].push(id);
  }

  const nodeWidth = 170;
  const nodeHeight = 148;
  // Developer graph (vertical orientation) should pack tightly and start flush.
  // Builder graph (horizontal orientation) can keep a small frame padding.
  const padding = orientation === 'vertical' ? 0 : 16;

  // Horizontal vs vertical spacing.
  // - Horizontal mode: levels are columns; spacing is intentionally roomier.
  // - Vertical mode (Developer graph): levels are rows; keep it compact.
  // Use small, consistent gutters to reduce dead space.
  // Vertical: no horizontal gaps between nodes in the same level.
  // Horizontal: keep roomy spacing for readability in the builder.
  const colWidth = orientation === 'vertical' ? nodeWidth : 220;
  const rowHeight = orientation === 'vertical' ? (nodeHeight + 8) : 160;

  const positions: Record<string, { x: number; y: number; level: number }> = {};

  const levelIds: Record<number, string[]> = {};
  let maxRows = 1;
  for (let l = 0; l <= maxLevel; l++) {
    levelIds[l] = (groups[l] || []).slice();
    maxRows = Math.max(maxRows, levelIds[l].length || 1);
  }

  // Order nodes within a level using a barycenter heuristic based on already-placed parents.
  // Then center each column/row to keep the graph visually symmetric.
  if (orientation === 'horizontal') {
    for (let l = 0; l <= maxLevel; l++) {
      const ids = levelIds[l];
      if (l === 0) {
        ids.sort();
      } else {
        ids.sort((a, b) => {
          const aParents = incoming.get(a) || [];
          const bParents = incoming.get(b) || [];

          const baryY = (parents: string[]) => {
            let sum = 0;
            let count = 0;
            for (const p of parents) {
              const pos = positions[p];
              if (!pos) continue;
              sum += pos.y;
              count++;
            }
            return count ? sum / count : Number.POSITIVE_INFINITY;
          };

          const ba = baryY(aParents);
          const bb = baryY(bParents);
          if (ba !== bb) return ba - bb;
          return a.localeCompare(b);
        });
      }

      const colRows = ids.length || 1;
      const colOffsetRows = Math.max(0, (maxRows - colRows) / 2);
      const yStart = padding + colOffsetRows * rowHeight;

      ids.forEach((id, idx) => {
        positions[id] = {
          x: padding + l * colWidth,
          y: yStart + idx * rowHeight,
          level: l,
        };
      });
    }

    const width = padding * 2 + (maxLevel + 1) * colWidth + nodeWidth;
    const height = padding * 2 + maxRows * rowHeight + nodeHeight;

    return { positions, padding, colWidth, rowHeight, nodeWidth, nodeHeight, width, height, hasCycle, invalidEdges };
  }

  // Vertical orientation: levels are rows (Y by level), nodes spread across row in X.
  const maxCols = maxRows;
  for (let l = 0; l <= maxLevel; l++) {
    const ids = levelIds[l];
    if (l === 0) {
      ids.sort();
    } else {
      ids.sort((a, b) => {
        const aParents = incoming.get(a) || [];
        const bParents = incoming.get(b) || [];

        const baryX = (parents: string[]) => {
          let sum = 0;
          let count = 0;
          for (const p of parents) {
            const pos = positions[p];
            if (!pos) continue;
            sum += pos.x;
            count++;
          }
          return count ? sum / count : Number.POSITIVE_INFINITY;
        };

        const ba = baryX(aParents);
        const bb = baryX(bParents);
        if (ba !== bb) return ba - bb;
        return a.localeCompare(b);
      });
    }

    // Pack rows to the left (no centering offsets) to avoid large empty gaps.
    const rowOffsetCols = 0;

    ids.forEach((id, idx) => {
      positions[id] = {
        x: padding + (rowOffsetCols + idx) * colWidth,
        y: padding + l * rowHeight,
        level: l,
      };
    });
  }

  const width = padding * 2 + maxCols * colWidth;
  const height = padding * 2 + (maxLevel + 1) * rowHeight;

  return { positions, padding, colWidth, rowHeight, nodeWidth, nodeHeight, width, height, hasCycle, invalidEdges };
}

export function toDag(
  defKey: string,
  nodes: StepNode[],
  edges: Edge[],
  inputsSchema: unknown,
  outputsSchema: unknown,
  outputsBindings: unknown,
  settings: Settings,
): DagDefinition {
  const steps: Record<string, any> = {};
  for (const n of nodes) {
    steps[n.id] = {
      type: n.def.type,
      service: n.def.service,
      device_capability: n.def.type === 'local' ? (n.def.device_capability || '') : '',
      ...(n.def.when ? { when: n.def.when } : {}),
      params_defaults: safeParseJson(n.paramsDefaultsJson),
      params_bindings: safeParseJson(n.paramsBindingsJson),
      override_allowlist: safeParseJson(n.overrideAllowlistJson),
      retries: {
        count: n.def.retries.count,
        backoff: n.def.retries.backoff,
      },
    };
  }

  return {
    workflow_id: defKey,
    inputs_schema: inputsSchema,
    outputs_schema: outputsSchema,
    outputs_bindings: outputsBindings,
    steps,
    edges: edges.map((e) => ({ from: e.from, to: e.to })),
    settings: {
      max_concurrency: settings.max_concurrency,
      timeout_seconds: settings.timeout_seconds,
      default_retry: {
        count: settings.default_retry.count,
        backoff: settings.default_retry.backoff,
      },
      ...(settings.empty_selection_policy ? { empty_selection_policy: settings.empty_selection_policy } : {}),
      ...(settings.run_idempotency ? { run_idempotency: settings.run_idempotency } : {}),
    },
  };
}

function normalizeRetry(value: unknown): StepRetryPolicy {
  const obj = coerceObject(value);
  return {
    count: Number.isFinite(Number(obj.count)) ? Number(obj.count) : 0,
    backoff: typeof obj.backoff === 'string' ? obj.backoff : 'linear',
  };
}

export function fromDag(definition: unknown): {
  workflow_id: string;
  nodes: StepNode[];
  edges: Edge[];
  inputsSchema: unknown;
  outputsSchema: unknown;
  outputsBindings: unknown;
  settings: Settings;
} {
  const raw = coerceObject(definition);
  // Back/forward-compat:
  // - Some endpoints store the DAG as the definition itself.
  // - Others wrap it as { "dag": { ... } }.
  const obj = (raw.dag && typeof raw.dag === 'object' && !Array.isArray(raw.dag)) ? coerceObject(raw.dag) : raw;
  const workflow_id = typeof obj.workflow_id === 'string' ? obj.workflow_id : '';
  const inputsSchema = (obj.inputs_schema ?? obj.inputs ?? {});
  const outputsSchema = (obj.outputs_schema ?? obj.outputs ?? {});
  const outputsBindings = (obj.outputs_bindings ?? obj.output_bindings ?? {});
  const stepsObj = coerceObject(obj.steps);
  const stepIds = Object.keys(stepsObj);

  const nodes: StepNode[] = stepIds.map((id) => {
    const s = coerceObject(stepsObj[id]);
    const type = s.type === 'local' ? 'local' : 'cloud';
    const retries = normalizeRetry(s.retries);
    const whenObj = coerceObject(s.when);
    const when = (Object.keys(whenObj).length > 0)
      ? {
          ...(Array.isArray(whenObj.has) ? { has: whenObj.has.filter((x: any) => typeof x === 'string') } : {}),
          ...(Array.isArray(whenObj.has_any) ? { has_any: whenObj.has_any.filter((x: any) => typeof x === 'string') } : {}),
        }
      : undefined;

    // Template-only best practice: params_defaults + params_bindings + override_allowlist.
    // Back-compat: if old "params" exists, treat it as params_defaults.
    const paramsDefaults = (s.params_defaults ?? s.params ?? {});
    const paramsBindings = (s.params_bindings ?? {});
    const overrideAllowlist = (s.override_allowlist ?? []);

    return {
      id,
      x: 0,
      y: 0,
      def: {
        type,
        service: typeof s.service === 'string' ? s.service : '',
        device_capability: typeof s.device_capability === 'string' ? s.device_capability : '',
        ...(when ? { when } : {}),
        retries,
      },
      paramsDefaultsJson: formatJson(paramsDefaults),
      paramsBindingsJson: formatJson(paramsBindings),
      overrideAllowlistJson: formatJson(overrideAllowlist),
    };
  });

  const edgesArr: any[] = Array.isArray(obj.edges) ? obj.edges : [];
  const edges: Edge[] = edgesArr
    .map((e, idx) => {
      const eo = coerceObject(e);
      const from = typeof eo.from === 'string' ? eo.from : '';
      const to = typeof eo.to === 'string' ? eo.to : '';
      if (!from || !to) return null;
      return { id: `e${idx}_${from}_${to}`, from, to } as Edge;
    })
    .filter(Boolean) as Edge[];

  const settingsObj = coerceObject(obj.settings);
  const runIdemObj = coerceObject((settingsObj as any).run_idempotency);
  const runIdemNamespace = typeof (runIdemObj as any).scope_namespace === 'string' ? String((runIdemObj as any).scope_namespace) : undefined;
  const runIdemFieldsRaw = Array.isArray((runIdemObj as any).scope_key_fields) ? (runIdemObj as any).scope_key_fields : [];
  const runIdemFields = runIdemFieldsRaw.map((x: any) => String(x ?? '').trim()).filter(Boolean);

  const settings: Settings = {
    max_concurrency: Number.isFinite(Number(settingsObj.max_concurrency)) ? Number(settingsObj.max_concurrency) : 1,
    timeout_seconds: Number.isFinite(Number(settingsObj.timeout_seconds)) ? Number(settingsObj.timeout_seconds) : 3600,
    default_retry: normalizeRetry(settingsObj.default_retry),
    empty_selection_policy: (settingsObj.empty_selection_policy === 'error' || settingsObj.empty_selection_policy === 'noop')
      ? settingsObj.empty_selection_policy
      : undefined,
    ...(runIdemNamespace || runIdemFields.length > 0
      ? { run_idempotency: { ...(runIdemNamespace ? { scope_namespace: runIdemNamespace } : {}), ...(runIdemFields.length > 0 ? { scope_key_fields: runIdemFields } : {}) } }
      : {}),
  };

  return {
    workflow_id,
    nodes,
    edges,
    inputsSchema,
    outputsSchema,
    outputsBindings,
    settings,
  };
}
